import { useState, useEffect, useCallback } from "react";
import type {
  FlowStepViewModel,
  FlowConfigViewModel,
  LLMConfigOptionsViewModel,
  ProviderType,
} from "@models/doctype";
import type { ProviderViewModel } from "@models/provider";
import type { ModelBasicInfoViewModel, ModelViewModel } from "@models/model";
import { useFlowConfig } from "./useFlowConfig";
import { useAdminPermissions } from "@hooks/useAdminPermissions";
import type {
  FlowConfigFormData,
  LLMStepConfigFormData,
  OCRStepConfigFormData,
  AgentExecutionFormData,
} from "@utils/builders/flowConfig";
import { buildCreateFlowConfigRequest } from "@utils/builders/flowConfig";
import { ProviderType as ProviderTypeEnum } from "@models/doctype";

interface UseStepEditProps {
  open: boolean;
  step: FlowStepViewModel;
  flowConfig: FlowConfigViewModel;
  docTypeId: string;
  initialTab?: string;
}

export const useStepEdit = ({
  open,
  step,
  flowConfig,
  docTypeId,
  initialTab,
}: UseStepEditProps) => {
  const {
    createFlowConfig,
    fetchProviders,
    fetchModelsBasicByProviderKey,
    fetchModelDetailByName,
    isLoading,
  } = useFlowConfig();
  const { canCreateDoctype } = useAdminPermissions();
  const [activeTab, setActiveTab] = useState<string>("0");
  const [hasChanges, setHasChanges] = useState(false);
  const [providers, setProviders] = useState<ProviderViewModel[]>([]);
  const [models, setModels] = useState<ModelBasicInfoViewModel[]>([]);
  const [selectedProvider, setSelectedProvider] = useState<string>("");
  const [selectedModel, setSelectedModel] = useState<ModelViewModel | null>(
    null,
  );
  const [isLoadingProviders, setIsLoadingProviders] = useState(false);
  const [isLoadingModels, setIsLoadingModels] = useState(false);

  // LLM Config State
  const [modelName, setModelName] = useState("");
  const [systemPrompt, setSystemPrompt] = useState("");
  const [userPrompt, setUserPrompt] = useState("");
  const [temperature, setTemperature] = useState<number | string>("");
  const [topP, setTopP] = useState<number | string>("");
  const [topK, setTopK] = useState<number | string>("");
  const [maxOutputTokens, setMaxOutputTokens] = useState<number | string>("");
  const [thinkingBudget, setThinkingBudget] = useState<number | string>("");
  const [seed, setSeed] = useState<number | string>("");
  const [responseSchema, setResponseSchema] = useState("");

  // OCR Config State
  const [ocrModelName, setOcrModelName] = useState("");
  const [ocrOptions, setOcrOptions] = useState("");

  // Agent Executions State
  const [agents, setAgents] = useState<AgentExecutionFormData[]>([]);
  const [llmProviders, setLlmProviders] = useState<ProviderViewModel[]>([]);
  const [llmModelsMap, setLlmModelsMap] = useState<
    Record<string, ModelBasicInfoViewModel[]>
  >({});

  const hasAgents = Array.isArray(step.llmStepConfig?.executions);

  useEffect(() => {
    if (open && initialTab !== undefined) {
      setActiveTab(initialTab);
    } else if (open) {
      setActiveTab("0");
    }
  }, [open, initialTab]);

  useEffect(() => {
    const initializeDialog = async () => {
      if (!open) return;

      setIsLoadingProviders(true);
      try {
        // Skip fetching providers list when user lacks provider permissions.
        // ProviderModelSelect will show read-only values from the step config.
        const loadedProviders = canCreateDoctype ? await loadProviders() : [];

        if (step.llmStepConfig) {
          const providerForStep = loadedProviders.find(
            (p) => p.key === step.llmStepConfig?.provider,
          );
          // In read-only mode (no provider perms), use the provider key directly
          setSelectedProvider(providerForStep?.id || step.llmStepConfig.provider || "");
          setModelName(step.llmStepConfig.modelName);
          setSystemPrompt(step.llmStepConfig.systemPrompt);
          setUserPrompt(step.llmStepConfig.userPrompt);

          const options = step.llmStepConfig.configOptions;
          setTemperature(options?.temperature?.toString() || "");
          setTopP(options?.topP?.toString() || "");
          setTopK(options?.topK?.toString() || "");
          setMaxOutputTokens(options?.maxOutputTokens?.toString() || "");
          setThinkingBudget(options?.thinkingBudget?.toString() || "");
          setSeed(options?.seed?.toString() || "");

          setResponseSchema(
            step.llmStepConfig.responseSchema
              ? JSON.stringify(step.llmStepConfig.responseSchema, null, 2)
              : "",
          );

          if (step.llmStepConfig.provider && canCreateDoctype) {
            await Promise.all([
              loadModels(step.llmStepConfig.provider),
              loadSelectedModelDetail(step.llmStepConfig.modelName),
            ]);
          } else if (step.llmStepConfig.modelName) {
            // Read-only: set a minimal model object for display
            setSelectedModel({
              modelName: step.llmStepConfig.modelName,
              displayName: step.llmStepConfig.modelName,
            } as ModelViewModel);
          }

          // Initialize agent executions
          if (step.llmStepConfig.executions?.length) {
            setLlmProviders(loadedProviders);
            setAgents(
              step.llmStepConfig.executions.map((a) => ({
                domainKey: a.domainKey,
                userPrompt: a.userPrompt,
                responseSchema: a.responseSchema,
                responseSchemaText: a.responseSchema
                  ? JSON.stringify(a.responseSchema, null, 2)
                  : "",
                systemPrompt: a.systemPrompt || "",
                modelName: a.modelName || "",
                configOptions: a.configOptions,
              })),
            );
          }
        }
        if (step.ocrStepConfig) {
          const providerForStep = loadedProviders.find(
            (p) => p.key === step.ocrStepConfig?.provider,
          );
          // In read-only mode, use the provider key directly
          setSelectedProvider(providerForStep?.id || step.ocrStepConfig.provider || "");
          setOcrModelName(step.ocrStepConfig.modelName);
          setOcrOptions(
            step.ocrStepConfig.ocrOptions
              ? JSON.stringify(step.ocrStepConfig.ocrOptions, null, 2)
              : "",
          );

          if (step.ocrStepConfig.provider && canCreateDoctype) {
            await Promise.all([
              loadModels(step.ocrStepConfig.provider),
              loadSelectedModelDetail(step.ocrStepConfig.modelName),
            ]);
          } else if (step.ocrStepConfig.modelName) {
            // Read-only: set a minimal model object for display
            setSelectedModel({
              modelName: step.ocrStepConfig.modelName,
              displayName: step.ocrStepConfig.modelName,
            } as ModelViewModel);
          }
        }
        setHasChanges(false);
      } finally {
        setIsLoadingProviders(false);
      }
    };

    initializeDialog();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, open]);

  const loadProviders = async (): Promise<ProviderViewModel[]> => {
    const providerType: ProviderType = step.ocrStepConfig
      ? ProviderTypeEnum.OCR
      : ProviderTypeEnum.LLM;
    const providersList = await fetchProviders(providerType);
    setProviders(providersList);
    return providersList;
  };

  const loadModels = async (providerKey: string) => {
    setIsLoadingModels(true);
    try {
      const modelsList = await fetchModelsBasicByProviderKey(providerKey);
      setModels(modelsList);
    } finally {
      setIsLoadingModels(false);
    }
  };

  const loadSelectedModelDetail = async (modelName: string) => {
    setIsLoadingModels(true);
    try {
      const model = await fetchModelDetailByName(modelName);
      setSelectedModel(model);
    } finally {
      setIsLoadingModels(false);
    }
  };

  const handleProviderChange = async (providerId: string) => {
    const provider = providers.find((p) => p.id === providerId);
    if (!provider) return;

    setSelectedProvider(providerId);
    setModels([]);
    setSelectedModel(null);
    setModelName("");
    setOcrModelName("");
    markAsChanged();

    if (provider.key) {
      await loadModels(provider.key);
    }
  };

  const handleModelChange = (modelName: string) => {
    loadSelectedModelDetail(modelName);
    if (step.llmStepConfig) {
      setModelName(modelName);
    } else if (step.ocrStepConfig) {
      setOcrModelName(modelName);
    }
    markAsChanged();
  };

  const handleLoadAgentModels = useCallback(
    async (providerKey: string, _providerType: "ocr" | "llm") => {
      const modelsList = await fetchModelsBasicByProviderKey(providerKey);
      setLlmModelsMap((prev) => ({ ...prev, [providerKey]: modelsList }));
    },
    [fetchModelsBasicByProviderKey],
  );

  const handleAgentsChange = (newAgents: AgentExecutionFormData[]) => {
    setAgents(newAgents);
    markAsChanged();
  };

  const markAsChanged = () => {
    if (!hasChanges) setHasChanges(true);
  };

  const handleSave = async () => {
    const selectedProviderObj = providers.find(
      (p) => p.id === selectedProvider,
    );
    const providerKey = selectedProviderObj?.key || "";

    const updatedSteps = flowConfig.steps.map((s) => {
      if (s.stepOrder === step.stepOrder) {
        if (step.llmStepConfig) {
          const configOptions: LLMConfigOptionsViewModel = {};
          if (temperature !== "")
            configOptions.temperature = Number(temperature);
          if (topP !== "") configOptions.topP = Number(topP);
          if (topK !== "") configOptions.topK = Number(topK);
          if (maxOutputTokens !== "")
            configOptions.maxOutputTokens = Number(maxOutputTokens);
          if (thinkingBudget !== "")
            configOptions.thinkingBudget = Number(thinkingBudget);
          if (seed !== "") configOptions.seed = Number(seed);

          const llmConfig: LLMStepConfigFormData = {
            provider: providerKey,
            modelName,
            systemPrompt,
            userPrompt,
            configOptions:
              Object.keys(configOptions).length > 0 ? configOptions : null,
            responseSchema: responseSchema.trim()
              ? JSON.parse(responseSchema)
              : null,
            executions: agents.length > 0 ? agents : null,
          };

          return {
            stepOrder: s.stepOrder,
            stepType: s.stepType,
            llmStepConfig: llmConfig,
          };
        }

        if (step.ocrStepConfig) {
          const ocrConfig: OCRStepConfigFormData = {
            provider: providerKey,
            modelName: ocrModelName,
            ocrOptions: ocrOptions.trim() ? JSON.parse(ocrOptions) : null,
          };

          return {
            stepOrder: s.stepOrder,
            stepType: s.stepType,
            ocrStepConfig: ocrConfig,
          };
        }
      }

      return {
        stepOrder: s.stepOrder,
        stepType: s.stepType,
        llmStepConfig: s.llmStepConfig
          ? {
              provider: s.llmStepConfig.provider,
              systemPrompt: s.llmStepConfig.systemPrompt,
              userPrompt: s.llmStepConfig.userPrompt,
              modelName: s.llmStepConfig.modelName,
              configOptions: s.llmStepConfig.configOptions,
              responseSchema: s.llmStepConfig.responseSchema,
              executions:
                s.llmStepConfig.executions?.map((a) => ({
                  domainKey: a.domainKey,
                  userPrompt: a.userPrompt,
                  responseSchema: a.responseSchema,
                  responseSchemaText: JSON.stringify(a.responseSchema, null, 2),
                  systemPrompt: a.systemPrompt || "",
                  modelName: a.modelName || "",
                  configOptions: a.configOptions,
                })) || null,
            }
          : undefined,
        ocrStepConfig: s.ocrStepConfig
          ? {
              provider: s.ocrStepConfig.provider,
              modelName: s.ocrStepConfig.modelName,
              ocrOptions: s.ocrStepConfig.ocrOptions,
            }
          : undefined,
      };
    });

    const formData: FlowConfigFormData = {
      flowBaseId: flowConfig.flowBaseId,
      flowType: flowConfig.flowType,
      flowDescription: flowConfig.flowDescription,
      steps: updatedSteps,
    };

    const request = buildCreateFlowConfigRequest(formData);
    const result = await createFlowConfig(docTypeId, request);

    if (result) {
      setHasChanges(false);
    }

    return result;
  };

  return {
    activeTab,
    hasChanges,
    providers,
    models,
    selectedProvider,
    selectedModel,
    isLoadingProviders,
    isLoadingModels,
    isLoading,
    modelName,
    systemPrompt,
    userPrompt,
    temperature,
    topP,
    topK,
    maxOutputTokens,
    thinkingBudget,
    seed,
    responseSchema,
    ocrModelName,
    ocrOptions,
    // Agent executions
    hasAgents,
    agents,
    llmProviders,
    llmModelsMap,
    setActiveTab,
    setSystemPrompt,
    setUserPrompt,
    setTemperature,
    setTopP,
    setTopK,
    setMaxOutputTokens,
    setThinkingBudget,
    setSeed,
    setResponseSchema,
    setOcrOptions,
    handleProviderChange,
    handleModelChange,
    handleSave,
    markAsChanged,
    handleAgentsChange,
    handleLoadAgentModels,
  };
};
